import { getHasValidCustomFieldValue } from "../fieldHelpers";
import {
    getCustomFieldsFromReceipt,
    getFieldsOptions,
    groupFieldsById,
} from "./getFieldsOptionsHelpers";

/**
 * @typedef {import("@qgiv/donation-form").ReduxTypes.DonationDetails.CustomFields} CustomFields
 * @typedef {import("@qgiv/donation-form").ReduxTypes.FieldSettings.CustomField} CustomField
 * @typedef {import("@qgiv/donation-form").ReduxTypes.Receipts.Receipt} Receipt
 * @typedef {import("@qgiv/donation-form").UtilityTypes.DataLayer.CustomFieldsOptions} CustomFieldsOptions
 */

/**
 * @param {object} options
 * @param {CustomFields} options.customFields
 * @param {CustomField[]} options.allDisplayableCustomFields
 * @returns {CustomFieldsOptions|{}}
 */
export const getCustomFieldsOptionsMultiStep = ({
    customFields = {},
    allDisplayableCustomFields = [],
}) => {
    const customFieldsOptions = {};
    const hasCustomFields = Object.keys(customFields).length > 0;

    if (!hasCustomFields) {
        return customFieldsOptions;
    }

    const idsOfCustomFields = Object.keys(customFields);

    // Add a key to the payload where that contains the value of the custom
    // field if the custom field has been filled out by the donor. Includes a
    // guard case that doesn't try to access data on this object as not doing
    // this could result in a fatal error
    const addCustomFieldValue = (idOfCustomField) => {
        const value = customFields[idOfCustomField];
        const customField =
            Array.isArray(allDisplayableCustomFields) &&
            allDisplayableCustomFields.find(
                (displayableCustomField) =>
                    displayableCustomField.id === Number(idOfCustomField),
            );

        if (customField && customField.ref) {
            const reportingLabel = customField.ref;
            const hasValue = getHasValidCustomFieldValue(customField, value);

            if (hasValue) {
                customFieldsOptions[reportingLabel] = value;
            }
        }
    };

    idsOfCustomFields.forEach(addCustomFieldValue);

    return customFieldsOptions;
};

/**
 * @description Returns custom fields options for a single-step form.
 * Note that fields array contains custom fields (context: FieldType.QUESTION) and
 * dedication fields (context: FieldType.DEDICATION).
 * @param {Receipt & { transaction: NonNullable<Receipt['transaction']>} } receipt
 * @returns {CustomFieldsOptions|{}}
 */
export const getCustomFieldsOptionsSingleStep = (receipt) => {
    let customFieldsOptions = {};

    const customFields = getCustomFieldsFromReceipt(receipt);
    const hasCustomFields = customFields.length > 0;

    if (hasCustomFields) {
        const customFieldsOptionsById = groupFieldsById(customFields);
        customFieldsOptions = getFieldsOptions(customFieldsOptionsById);
    }

    return customFieldsOptions;
};
